// @ts-strict-ignore
import type { GetFakeThingProps } from "./types";
import type { RemoveFirstArgument } from "@lib/typeHelpers";
import type { FilteredDashboardApiEndpointKeys } from "../types";
import type { dashboardApiEndpoints } from "../dashboardApiEndpoints";

import { getFakeFailingGetFetcherFunction } from "./getFakeFailingGetFetcherFunction";
import { getFakeSuccessGetFetcherFunction } from "./getFakeSuccessGetFetcherFunction";
import { getFakeLoadingGetFetcherFunction } from "./getFakeLoadingGetFetcherFunction";

/**
 * Return fake fetcher functions for storybook usage
 *
 * This returns 3 different fake fetcher functions.
 * - resolved
 * - rejected
 * - pending
 *
 * **rejected** always rejects with an error message, and can be used to test how the component reacts to a failed fetch
 * **pending** returns a promise that never resolves, and can be used to test how the component renders in a loading state
 * **resolved** returns a function that accepts an optional `maker` function, which in turn returns a fetcher that resolves with a fake value
 *
 * The `maker` function takes the same arguments passed to the real fetcher function, and should return a partial version of the shape returned
 * from the payload. This is in turn merged in with the default fake so that all fields are present.
 *
 * For example for the developer endpoint, you can test requesting a developer from a different company and ensure the company Id is valid:
 *
 * ```
 * // create a fetcher function that increments sign-in count of the developer every time it is called
 * const fetcherFunction = fakeDeveloperFetchers.resolved(
 *   (companyId: string) => ({ company_id: companyId })
 * );
 * ```
 *
 * As the maker is optional, if you always want to return the static fake, just call the maker function
 * ```
 * // create a fetcher function that increments sign-in count of the developer every time it is called
 * const fetcherFunction = fakeDeveloperFetchers.resolved();
 * ```
 *
 * The pending/rejected fetchers should be passed into `FakeDashboardApiProvider` `GET` override for the endpoint as-is
 */
export const getFakeGetFetcherFunctions = <
  PayloadShape,
  CacheKey extends FilteredDashboardApiEndpointKeys,
  Args extends RemoveFirstArgument<Parameters<(typeof dashboardApiEndpoints)[CacheKey]["GET"]>[0]["queryKey"]>,
>(
  props: GetFakeThingProps<PayloadShape, CacheKey>
) => {
  return {
    resolved: getFakeSuccessGetFetcherFunction<PayloadShape, CacheKey, Args>(props),
    rejected: getFakeFailingGetFetcherFunction<PayloadShape, CacheKey, Args>(props),
    pending: getFakeLoadingGetFetcherFunction<PayloadShape, CacheKey, Args>(props),
  };
};
